import { useEffect, useCallback, useRef } from 'react';

export interface UnsavedChangesConfig {
  hasUnsavedChanges: boolean;
  warningMessage?: string;
}

export interface UnsavedChangesHandlers {
  handleLogoClick: () => void;
  handleLogoutClick: (onLogout: () => void) => void;
}

/**
 * Custom hook for handling unsaved changes protection
 * Provides beforeunload, popstate, and navigation handlers
 */
export const useUnsavedChanges = (config: UnsavedChangesConfig): UnsavedChangesHandlers => {
  const {
    hasUnsavedChanges,
    warningMessage = ''
  } = config;

  const isInitialized = useRef(false);

  // Handle beforeunload event (close tab, reload) - browser native alert
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Use default browser popup
        return '';
      }
    };

    const handlePopState = () => {
      if (hasUnsavedChanges) {
        // Prevent back navigation by pushing current state
        window.history.pushState(null, '', window.location.href);
        
        // No custom alert - only browser alert
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to enable popstate detection
    if (!isInitialized.current) {
      window.history.pushState(null, '', window.location.href);
      isInitialized.current = true;
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, warningMessage]);

  // Custom navigation handlers - no custom alerts
  const handleLogoClick = useCallback(() => {
    window.location.href = '/';
  }, []);

  const handleLogoutClick = useCallback((onLogout: () => void) => {
    // Use default browser popup by triggering beforeunload
    if (hasUnsavedChanges) {
      const event = new Event('beforeunload');
      window.dispatchEvent(event);
    }
    onLogout();
  }, [hasUnsavedChanges]);

  return {
    handleLogoClick,
    handleLogoutClick
  };
};

/**
 * Service for operation in progress protection
 * Used for list pages where operations might be in progress
 */
export interface OperationInProgressConfig {
  operationInProgress: boolean;
  warningMessage?: string;
}

export const useOperationInProgress = (config: OperationInProgressConfig): UnsavedChangesHandlers => {
  const {
    operationInProgress,
    warningMessage = '操作が進行中です。このページを離れますか？'
  } = config;

  const isInitialized = useRef(false);

  // Handle beforeunload event (close tab, reload) - browser native alert
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (operationInProgress) {
        e.preventDefault();
        e.returnValue = ''; // Use default browser popup
        return '';
      }
    };

    const handlePopState = () => {
      if (operationInProgress) {
        // Prevent back navigation by pushing current state
        window.history.pushState(null, '', window.location.href);
        
        // No custom alert - only browser alert
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to enable popstate detection
    if (!isInitialized.current) {
      window.history.pushState(null, '', window.location.href);
      isInitialized.current = true;
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [operationInProgress, warningMessage]);

  // Custom navigation handlers - no custom alerts
  const handleLogoClick = useCallback(() => {
    window.location.href = '/';
  }, []);

  const handleLogoutClick = useCallback((onLogout: () => void) => {
    // Use default browser popup by triggering beforeunload
    if (operationInProgress) {
      const event = new Event('beforeunload');
      window.dispatchEvent(event);
    }
    onLogout();
  }, [operationInProgress]);

  return {
    handleLogoClick,
    handleLogoutClick
  };
};
